import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import SEO from './SEO';
import './FeaturesPage.css';

const categories = [
  { id: "all", label: "All Features", icon: "💎" },
  { id: "proctoring", label: "Smart Proctoring", icon: "🛡️" },
  { id: "collaboration", label: "Collaboration", icon: "👥" },
  { id: "ai", label: "AI & Analytics", icon: "🤖" },
  { id: "platform", label: "SaaS Enterprise", icon: "🏢" }
];

const featuresData = [
  {
    title: "AI Conversational Interviewer",
    category: "ai",
    description: "Automate screenings with a natural, conversational AI voice assistant that dynamically adapts to each candidate.",
    bullets: [
      "Resume-customized question generation",
      "Real-time natural speech voice synthesis",
      "Instant response evaluation & grading feedback"
    ],
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"></circle>
        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
        <line x1="12" y1="17" x2="12.01" y2="17"></line>
        <path d="M12 8v4M8 12h8"></path>
      </svg>
    )
  },
  {
    title: "Smart Eye Tracking",
    category: "proctoring",
    description: "Monitors candidate head and gaze movement in real-time using lightweight browser-based face mesh tracking to ensure maximum exam integrity.",
    bullets: [
      "Zero-plugin browser-native gaze processing",
      "Instant look-away duration threshold alerts",
      "Comprehensive gaze status and deviation logging"
    ],
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
        <circle cx="12" cy="12" r="3"></circle>
      </svg>
    )
  },
  {
    title: "Smart Lip Sync & Voice",
    category: "proctoring",
    description: "Analyzes audio-video coherence dynamically on speech detection, preventing audio hijacking, deepfakes, or proxy voice-overs.",
    bullets: [
      "Automatic video snippet triggers during speech",
      "AI-powered audio-to-video alignment verification",
      "Critical warning alerts for synthetic audio signatures"
    ],
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
        <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
        <line x1="12" y1="19" x2="12" y2="23"></line>
        <line x1="8" y1="23" x2="16" y2="23"></line>
      </svg>
    )
  },
  {
    title: "Stealth Screenshot Proctoring",
    category: "proctoring",
    description: "Periodic background viewport and camera captures with an offline buffer to track developer engagement non-intrusively.",
    bullets: [
      "Role-based identity and timestamp watermarks",
      "Encrypted, session-isolated storage folder",
      "Fail-safe local storage queue for flaky networks"
    ],
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
        <circle cx="12" cy="13" r="4"></circle>
      </svg>
    )
  },
  {
    title: "Collaborative IDE",
    category: "collaboration",
    description: "Collaborate inside a full developer workspace featuring sync code, remote compile, and direct judge executions.",
    bullets: [
      "Syntax-highlighting for 10+ popular languages",
      "Real-time dual-cursor sync & remote input state",
      "Direct code runner output & judge validation"
    ],
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="16 18 22 12 16 6"></polyline>
        <polyline points="8 6 2 12 8 18"></polyline>
      </svg>
    )
  },
  {
    title: "Collaborative Whiteboard",
    category: "collaboration",
    description: "Shared interactive sketchpad enabling candidate and interviewer to design system architectures and flowcharts side-by-side.",
    bullets: [
      "Synchronized vector shapes, text, and drawing tools",
      "Smooth panning, zoom, and collaborative grid canvas",
      "Complete whiteboard snapshot logs saved in results"
    ],
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 20h9"></path>
        <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
      </svg>
    )
  },
  {
    title: "Keystroke Playback & Replay",
    category: "collaboration",
    description: "Replay the candidate's coding process keystroke-by-keystroke to analyze real problem-solving speeds and writing sequences.",
    bullets: [
      "Interactive media control controls (play, pause, speed)",
      "Accurate state restoration from compressed JSON log files",
      "Dynamic timeline markers pointing to major key milestones"
    ],
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 8v4l3 3"></path>
        <circle cx="12" cy="12" r="10"></circle>
        <polyline points="12 2 12 6"></polyline>
      </svg>
    )
  },
  {
    title: "Multi-Tenant Workspace Isolation",
    category: "platform",
    description: "Enterprise multi-tenancy securing organization-isolated databases and custom domains for total data sovereignty.",
    bullets: [
      "Strict data isolation via dynamic tenant selectors",
      "Custom domain and subdomain branding URL bindings",
      "AES-256 GCM encrypted profiles & interview assets"
    ],
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
        <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
      </svg>
    )
  },
  {
    title: "Smart Recruiter Scheduler",
    category: "platform",
    description: "Create calendar slots, sync recruiter timetables, and coordinate active rooms automatically inside visual calendars.",
    bullets: [
      "Seamless calendar view timeline dashboard mappings",
      "One-click WebRTC interview room slot generators",
      "Google Calendar, Microsoft Outlook, and custom syncs"
    ],
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
        <line x1="16" y1="2" x2="16" y2="6"></line>
        <line x1="8" y1="2" x2="8" y2="6"></line>
        <line x1="3" y1="10" x2="21" y2="10"></line>
      </svg>
    )
  },
  {
    title: "Detailed Assessment Reports",
    category: "ai",
    description: "Generates thorough scorecard metrics and grading analyses mapping candidate soft, behavioral, and hard skill metrics.",
    bullets: [
      "Subject-matter scorecard grading dashboards",
      "Complete chronological text transcriptions",
      "AI-reconstructed feedback reports & hiring tags"
    ],
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="20" x2="18" y2="10"></line>
        <line x1="12" y1="20" x2="12" y2="4"></line>
        <line x1="6" y1="20" x2="6" y2="14"></line>
      </svg>
    )
  },
  {
    title: "HR Team Collaboration",
    category: "platform",
    description: "Invite coordinators, manage recruiter permissions, and audit interviewer logs inside a master SaaS panel.",
    bullets: [
      "Granular role authorization (Admins, Recruiters, Interviewers)",
      "Shared interviewer slots and schedule planners",
      "Activity audit logs monitoring candidate reports"
    ],
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
        <circle cx="9" cy="7" r="4"></circle>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
      </svg>
    )
  }
];

// Spotlight Hover Card Component with 3D Tilt Effect
const FeatureCard = ({ feature, index }) => {
  const cardRef = useRef(null);

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    // Set spotlight custom properties
    cardRef.current.style.setProperty('--mouse-x', `${x}px`);
    cardRef.current.style.setProperty('--mouse-y', `${y}px`);

    // Dynamic 3D Card Rotation based on cursor proximity
    const cardWidth = rect.width;
    const cardHeight = rect.height;
    const centerX = cardWidth / 2;
    const centerY = cardHeight / 2;
    const rotateX = ((y - centerY) / centerY) * 8; // Max 8deg tilt vertical
    const rotateY = ((centerX - x) / centerX) * 8; // Max 8deg tilt horizontal

    cardRef.current.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateY(-8px)`;
  };

  const handleMouseLeave = () => {
    if (!cardRef.current) return;
    // Reset transform smoothly
    cardRef.current.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) translateY(0px)';
  };

  return (
    <div
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className={`feature-card feature-card-reveal category-${feature.category}`}
      style={{ transitionDelay: `${index * 50}ms` }}
    >
      {/* Category Indicator Badge */}
      <span className="card-category-badge">{feature.category.toUpperCase()}</span>
      
      {/* Glowing Border Background */}
      <div className="card-border-glow"></div>
      
      {/* Card Content Wrapper */}
      <div className="feature-icon-wrapper">{feature.icon}</div>
      <h3>{feature.title}</h3>
      <p>{feature.description}</p>
      
      <ul className="feature-bullets">
        {feature.bullets.map((bullet, idx) => (
          <li key={idx}>
            <span className="feature-bullet-icon">✓</span>
            <span>{bullet}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const FeaturesPage = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isLoggedIn = !!user && !!user.id;
  const userName = user.full_name || '';

  const pageRef = useRef(null);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('all');

  // Scroll reveal setup using IntersectionObserver
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px -10% -10% 0px',
      threshold: 0.05
    };

    const handleIntersection = (entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          observer.unobserve(entry.target);
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersection, observerOptions);
    const revealElements = pageRef.current?.querySelectorAll('.feature-card-reveal, .scroll-reveal');

    if (revealElements) {
      revealElements.forEach(el => observer.observe(el));
    }

    return () => {
      if (revealElements) {
        revealElements.forEach(el => observer.unobserve(el));
      }
    };
  }, [activeTab]); // Re-observe when items change with activeTab filter

  const filteredFeatures = activeTab === 'all'
    ? featuresData
    : featuresData.filter(f => f.category === activeTab);

  return (
    <div className="features-container" ref={pageRef}>
      <SEO 
        title="Advanced Interview Features - Live Coding & AI Proctoring"
        description="Explore the features of Smart Interview, including collaborative whiteboards, proctored IDEs, stealth eye-tracking, and multi-tenant isolation."
        keywords="collaborative IDE, smart eye tracking, proctoring software, developer assessment, interview scheduler, multi-tenant workspace"
      />
      
      {/* Schema.org Structured Data detailing core platform features */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "Smart Interview Platform",
          "applicationCategory": "BusinessApplication",
          "featureList": [
            "AI Conversational Interviewer screening assistant",
            "Smart Gaze and Head tracking proctoring",
            "Lip Sync dynamic voice matching proctoring",
            "Stealth viewport and webcam captures",
            "Collaborative Monaco code sandbox and IDE supporting 10+ languages",
            "Real-time synchronized whiteboard canvas sketchpad",
            "Interactive keystroke logs restore playback",
            "Multi-tenant database container isolation",
            "Integrated team recruiter scheduler calendar"
          ]
        })}
      </script>
      {/* Modern Background Ambience Blobs */}
      <div className="features-glow-blob features-blob-1"></div>
      <div className="features-glow-blob features-blob-2"></div>
      <div className="features-glow-grid"></div>

      <Navbar isLoggedIn={isLoggedIn} userName={userName} />

      <main className="features-content">
        


        {/* Hero Section */}
        <section className="features-title-section scroll-reveal">
          <span className="features-tagline">ADVANCED ENGINE OVERVIEW</span>
          <h1>
            A Smarter Way to <span className="features-title-gradient">Evaluate Talent</span>
          </h1>
          <p>
            An all-in-one interview suite combining state-of-the-art AI proctoring, synchronized code sandboxes, and automated mock screening assessments.
          </p>
        </section>

        {/* Dynamic Category Filtering Tabs */}
        <div className="features-tabs-container scroll-reveal">
          <div className="features-tabs">
            {categories.map((cat) => (
              <button
                key={cat.id}
                className={`features-tab-btn ${activeTab === cat.id ? 'active' : ''}`}
                onClick={() => setActiveTab(cat.id)}
              >
                <span className="tab-icon">{cat.icon}</span>
                <span className="tab-label">{cat.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Grid of features */}
        <section className="features-grid" key={activeTab}>
          {filteredFeatures.map((feature, idx) => (
            <FeatureCard key={feature.title} feature={feature} index={idx} />
          ))}
        </section>

        {/* CTA Section */}
        <section className="features-cta-section scroll-reveal">
          <div className="features-cta-card">
            <div className="features-cta-glow"></div>
            <h2>Ready to transform your recruitment?</h2>
            <p>
              Experience secure enterprise workspaces, live proctored screenings, and deep automated AI assessments. Setup your tenant workspace in minutes.
            </p>
            <div className="features-cta-buttons">
              <button className="features-cta-btn primary" onClick={() => navigate('/signup')}>
                Get Started
              </button>
              <button className="features-cta-btn secondary" onClick={() => navigate('/login')}>
                Sign In
              </button>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
};

export default FeaturesPage;
